import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PromotionSchedule,
  PromotionScheduleCreateRequest,
  PromotionScheduleDeleteResponse,
  PromotionScheduleUpdateRequest,
  promotionScheduleApi,
} from '../../../../lib/api/promotionSchedule';
import { promotionScheduleQuery } from '../queries';

/**
 * 생성·수정은 타임라인과 변경 이력 트리를 함께 무효화한다.
 * (삭제는 상세·이력을 **제거**해야 해서 따로 간다 — `useDeleteScheduleMutation` 참고)
 *
 * - `timeline._def`: 조회 키가 `{ from, to, tracks, statuses, adminIds }` 조합이라
 *   특정 키만 골라 무효화하기 어렵고, 한 항목의 기간·트랙·상태가 바뀌면 다른 필터 조합의
 *   결과도 달라지기 때문에 통째로 비운다.
 * - `logs._def`: 모든 수정이 UpdateLog 를 남기므로, 이력 탭을 열어둔 상태에서도 새 이력이 보여야 한다.
 * - `list._def` · `detail._def` (AMZUWEB-682): 같은 항목을 리스트·상세에서도 본다.
 *   **상세에서 수정하면 그 화면 자신이 낡는다** — 모달을 닫은 뒤 헤더가 예전 제목인 채로 남는다.
 *   리스트도 마찬가지로 필터 조합마다 키가 달라 통째로 비운다.
 */
const useInvalidateSchedules = () => {
  const queryClient = useQueryClient();

  return () =>
    Promise.all([
      queryClient.invalidateQueries(promotionScheduleQuery.timeline._def),
      queryClient.invalidateQueries(promotionScheduleQuery.list._def),
      queryClient.invalidateQueries(promotionScheduleQuery.detail._def),
      queryClient.invalidateQueries(promotionScheduleQuery.logs._def),
    ]);
};

export const useCreateScheduleMutation = () => {
  const invalidateSchedules = useInvalidateSchedules();

  return useMutation<PromotionSchedule, Error, PromotionScheduleCreateRequest>({
    mutationFn: (body) => promotionScheduleApi.create(body),
    onSuccess: () => invalidateSchedules(),
  });
};

export const useUpdateScheduleMutation = () => {
  const invalidateSchedules = useInvalidateSchedules();

  return useMutation<PromotionSchedule, Error, { id: number; body: PromotionScheduleUpdateRequest }>({
    mutationFn: ({ id, body }) => promotionScheduleApi.update(id, body),
    onSuccess: () => invalidateSchedules(),
  });
};

/**
 * 🔴 **삭제는 무효화하면 안 되는 쿼리가 있다.**
 *
 * `invalidateQueries` 는 *활성* 쿼리를 곧바로 **재조회**한다. 상세 화면에서 삭제하면 그 화면의
 * 상세·이력 쿼리가 아직 떠 있는 상태라, 방금 지운 id 로 다시 물어보고 400 을 받는다.
 * 그 흐름이 `mutateAsync` 가 끝나기 전에 완료되므로 **삭제 성공 안내보다 먼저** 도착한다 —
 * 화면이 잠깐 "조회 실패" 로 뒤집힌다.
 *
 * 그래서 삭제된 대상은 무효화가 아니라 **제거**한다. 없어진 것을 다시 물어볼 이유가 없다.
 * 제거는 `onSuccess` 안, 즉 아직 아무 렌더도 일어나지 않은 시점이라 재조회를 유발하지 않는다.
 * 호출한 화면이 상세든 타임라인이든 같게 동작하므로 **여기 한 곳에만 둔다.**
 */
export const useDeleteScheduleMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<PromotionScheduleDeleteResponse, Error, number>({
    mutationFn: (id) => promotionScheduleApi.remove(id),
    onSuccess: (_result, id) => {
      queryClient.removeQueries(promotionScheduleQuery.detail(id));
      queryClient.removeQueries(promotionScheduleQuery.logs(id));

      // 목록류는 그 항목이 빠진 모습으로 다시 그려져야 하므로 평소대로 무효화한다
      return Promise.all([
        queryClient.invalidateQueries(promotionScheduleQuery.timeline._def),
        queryClient.invalidateQueries(promotionScheduleQuery.list._def),
      ]);
    },
  });
};
